import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Container from '../components/shared/Container';
import './EvidentPage.css';

/* ─────────────────────────────────────────────
   ENCODE ANIMATION
   Input text gets highlighted → code lines build up
   ───────────────────────────────────────────── */
const EncodeAnimation = () => {
  const [cycleKey, setCycleKey]   = useState(0);
  const [hlCount, setHlCount]     = useState(0);   // 0→1→2 highlighted spans
  const [codeStep, setCodeStep]   = useState(0);   // 0→7 code lines visible
  const [isOut, setIsOut]         = useState(false);
  const timeoutsRef = useRef([]);

  const clear = () => { timeoutsRef.current.forEach(clearTimeout); timeoutsRef.current = []; };
  const after = (fn, ms) => { const id = setTimeout(fn, ms); timeoutsRef.current.push(id); };

  useEffect(() => {
    clear();
    setHlCount(0); setCodeStep(0); setIsOut(false);

    const HL   = 750;   // per highlight step
    const WAIT = 600;   // before highlights start
    const GAP  = 600;   // pause before code starts
    const CODE = 330;   // per code line

    after(() => setHlCount(1), WAIT);
    after(() => setHlCount(2), WAIT + HL);

    const cs = WAIT + HL * 2 + GAP;
    for (let i = 1; i <= 7; i++) after(() => setCodeStep(i), cs + CODE * (i - 1));

    after(() => { setIsOut(true); after(() => setCycleKey(k => k + 1), 500); },
      cs + CODE * 6 + 2800);

    return clear;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleKey]);

  const codeLines = [
    { indent: 0, node: <span className="text-indigo-500 font-semibold">BENEFITS_ELIGIBILITY</span> },
    { indent: 1, node: <span className="text-gray-400">AND {'['}</span> },
    { indent: 3, node: <span className="text-gray-400">policy.active,</span> },
    { indent: 3, node: <span className="text-gray-400">employment_months <span className="text-indigo-400">≥ 14</span>,</span> },
    { indent: 3, node: <span className="text-gray-400">waiting_days <span className="text-indigo-400">≥ 90</span>,</span> },
    { indent: 3, node: <span className="text-gray-400">medical_evidence</span> },
    { indent: 1, node: <span className="text-gray-400">{']'}</span> },
  ];

  const hl = (active, text) => (
    <span style={{
      background:  active ? '#eef2ff' : 'transparent',
      color:       active ? '#4f46e5' : 'inherit',
      borderRadius: '3px',
      padding:     active ? '0 2px' : '0',
      transition:  'background 0.4s ease, color 0.4s ease, padding 0.2s ease',
    }}>{text}</span>
  );

  return (
    <div className={`rounded-2xl border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.04),0_8px_32px_rgba(0,0,0,0.08)] bg-white overflow-hidden`}
      style={{ opacity: isOut ? 0 : 1, transition: 'opacity 0.45s ease' }}>
      <div className="flex flex-col sm:flex-row sm:divide-x divide-gray-100">

        {/* Input */}
        <div className="flex-1 p-5 sm:p-7">
          <p className="text-[0.6rem] font-bold tracking-[0.1em] uppercase text-gray-400 mb-4">Freeform Description</p>
          <p className="text-[0.78rem] text-gray-500 leading-relaxed">
            "The insured must have been{' '}
            {hl(hlCount >= 1, 'continuously employed for at least 14 months')}
            {' '}prior to disability onset. The{' '}
            {hl(hlCount >= 2, '90-day elimination period')}
            {' '}must be fully elapsed before benefits commence..."
          </p>
        </div>

        {/* Arrow — hidden on mobile, shown on sm+ */}
        <div className="hidden sm:flex items-center px-3 bg-gray-50/60">
          <div style={{
            background: '#fff',
            border: `1px solid ${codeStep > 0 ? '#a5b4fc' : '#e5e7eb'}`,
            borderRadius: '50%',
            padding: '6px',
            boxShadow: codeStep > 0 ? '0 0 0 3px #eef2ff' : 'none',
            transition: 'border-color 0.3s, box-shadow 0.3s',
          }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 7h10M8 3l4 4-4 4"
                stroke={codeStep > 0 ? '#818cf8' : '#d1d5db'}
                strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ transition: 'stroke 0.3s' }}/>
            </svg>
          </div>
        </div>

        {/* Output */}
        <div className="flex-1 p-5 sm:p-7 bg-slate-50 border-t sm:border-t-0 border-gray-100">
          <p className="text-[0.6rem] font-bold tracking-[0.1em] uppercase text-gray-400 mb-4">Decision Program</p>
          <div className="font-mono text-[0.7rem] leading-loose">
            {codeLines.map((line, i) => codeStep > i && (
              <div key={i}
                style={{ paddingLeft: `${line.indent * 0.7}rem`, animation: 'apLineIn 0.28s ease both' }}>
                {line.node}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   VERIFY ANIMATION
   Rule conditions check off one by one
   ───────────────────────────────────────────── */
const VerifyAnimation = () => {
  const [cycleKey, setCycleKey]     = useState(0);
  const [checkCount, setCheckCount] = useState(0);   // 0→4
  const [isOut, setIsOut]           = useState(false);
  const timeoutsRef = useRef([]);

  const clear = () => { timeoutsRef.current.forEach(clearTimeout); timeoutsRef.current = []; };
  const after = (fn, ms) => { const id = setTimeout(fn, ms); timeoutsRef.current.push(id); };

  useEffect(() => {
    clear();
    setCheckCount(0); setIsOut(false);

    const WAIT = 700;
    const STEP = 820;

    for (let i = 1; i <= 4; i++) after(() => setCheckCount(i), WAIT + STEP * (i - 1));
    after(() => { setIsOut(true); after(() => setCycleKey(k => k + 1), 500); },
      WAIT + STEP * 3 + 2800);

    return clear;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleKey]);

  const rules = [
    'policy.active = true',
    'employment_months ≥ 14',
    'waiting_period_days ≥ 90',
    'medical_evidence provided',
  ];

  return (
    <div className="rounded-2xl border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.04),0_8px_32px_rgba(0,0,0,0.08)] bg-white overflow-hidden"
      style={{ opacity: isOut ? 0 : 1, transition: 'opacity 0.45s ease' }}>

      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-900">Benefits Eligibility</p>
          <p className="text-[0.68rem] text-gray-400 mt-0.5">Rule 3 of 8 · Assigned to J. Kowalski</p>
        </div>
        <span className="text-[0.62rem] font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
          In Review
        </span>
      </div>

      <div className="px-6 py-5">
        <p className="text-[0.62rem] font-semibold tracking-[0.08em] uppercase text-gray-400 mb-4">Conditions</p>
        <div className="space-y-3">
          {rules.map((rule, i) => {
            const done = checkCount > i;
            return (
              <div key={rule} className="flex items-center gap-3 pl-3">
                {done ? (
                  <svg width="15" height="15" viewBox="0 0 14 14" fill="none"
                    className="flex-shrink-0"
                    style={{ animation: 'apCheckIn 0.32s cubic-bezier(0.34,1.56,0.64,1) both' }}>
                    <circle cx="7" cy="7" r="6.5" fill="#f0fdf4" stroke="#4ade80" strokeWidth="1"/>
                    <path d="M3.5 7l2.5 2.5 4.5-4.5" stroke="#4ade80" strokeWidth="1.4"
                      strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <div className="w-[15px] h-[15px] rounded-full border-2 border-gray-200 flex-shrink-0"/>
                )}
                <span className={`text-[0.8rem] transition-colors duration-300 ${done ? 'text-gray-400' : 'text-gray-700 font-medium'}`}>
                  {rule}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-6 py-4 bg-gray-50/80 border-t border-gray-100 flex items-center justify-between">
        <p className="text-[0.68rem] text-gray-400">
          {checkCount} of {rules.length} verified
        </p>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          fontSize: '0.72rem', fontWeight: 600, color: '#fff',
          background: checkCount === rules.length ? '#4ade80' : '#818cf8',
          borderRadius: '8px', padding: '6px 12px',
          transition: 'background 0.4s ease',
          cursor: 'default',
        }}>
          {checkCount === rules.length ? 'Approved' : 'Approve Next'}
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
            <path d="M2 6h8M7 3l3 3-3 3" stroke="currentColor" strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   EVOLVE ANIMATION
   Chat conversation builds up, then deploy
   ───────────────────────────────────────────── */
const EvolveAnimation = () => {
  const [cycleKey, setCycleKey]         = useState(0);
  const [showUser, setShowUser]         = useState(false);
  const [showThinking, setShowThinking] = useState(false);
  const [showText, setShowText]         = useState(false);
  const [showCode, setShowCode]         = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [showDeploy, setShowDeploy]     = useState(false);
  const [isOut, setIsOut]               = useState(false);
  const timeoutsRef = useRef([]);

  const clear = () => { timeoutsRef.current.forEach(clearTimeout); timeoutsRef.current = []; };
  const after = (fn, ms) => { const id = setTimeout(fn, ms); timeoutsRef.current.push(id); };

  useEffect(() => {
    clear();
    setShowUser(false); setShowThinking(false); setShowText(false);
    setShowCode(false); setShowValidation(false); setShowDeploy(false);
    setIsOut(false);

    after(() => setShowUser(true),       600);
    after(() => setShowThinking(true),   1500);
    after(() => { setShowThinking(false); setShowText(true); }, 2600);
    after(() => setShowCode(true),       3400);
    after(() => setShowValidation(true), 4300);
    after(() => setShowDeploy(true),     5000);
    after(() => { setIsOut(true); after(() => setCycleKey(k => k + 1), 500); }, 8200);

    return clear;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleKey]);

  return (
    <div className="rounded-2xl border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.04),0_8px_32px_rgba(0,0,0,0.08)] bg-white overflow-hidden"
      style={{ opacity: isOut ? 0 : 1, transition: 'opacity 0.45s ease', height: '390px' }}>

      <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2.5">
        <div className="w-2 h-2 rounded-full bg-green-400"/>
        <span className="text-sm font-semibold text-gray-900">Evident</span>
        <span className="text-[0.62rem] text-gray-400 font-medium">Decision Updates</span>
      </div>

      <div className="px-5 py-5 space-y-4" style={{ minHeight: '220px' }}>

        {/* User message */}
        {showUser && (
          <div className="flex justify-end" style={{ animation: 'apMsgIn 0.35s ease both' }}>
            <div className="bg-gray-100 rounded-2xl rounded-tr-sm px-4 py-3 max-w-[82%]">
              <p className="text-[0.68rem] font-semibold text-gray-400 mb-1">J. Torres</p>
              <p className="text-[0.82rem] text-gray-800 leading-snug">
                Contractors should be excluded from benefits eligibility.
              </p>
            </div>
          </div>
        )}

        {/* Thinking */}
        {showThinking && (
          <div className="flex justify-start" style={{ animation: 'apMsgIn 0.3s ease both' }}>
            <div className="border border-gray-100 bg-slate-50 rounded-2xl rounded-tl-sm px-4 py-3">
              <p className="text-[0.68rem] font-semibold text-indigo-400 mb-2">Evident</p>
              <div className="flex gap-1.5 items-center">
                <span className="ap-dot w-1.5 h-1.5 rounded-full bg-indigo-300"/>
                <span className="ap-dot w-1.5 h-1.5 rounded-full bg-indigo-300"/>
                <span className="ap-dot w-1.5 h-1.5 rounded-full bg-indigo-300"/>
              </div>
            </div>
          </div>
        )}

        {/* Evident response */}
        {showText && (
          <div className="flex justify-start" style={{ animation: 'apMsgIn 0.35s ease both' }}>
            <div className="border border-gray-100 bg-slate-50 rounded-2xl rounded-tl-sm px-4 py-3.5 max-w-[90%]">
              <p className="text-[0.68rem] font-semibold text-indigo-500 mb-2">Evident</p>
              <p className="text-[0.8rem] text-gray-700 mb-3 leading-snug">
                Got it. Proposed addition to the eligibility program:
              </p>

              {showCode && (
                <div className="bg-white border border-gray-100 rounded-lg px-3 py-2.5 font-mono text-[0.68rem] text-gray-500 mb-3"
                  style={{ animation: 'apFadeIn 0.3s ease both' }}>
                  <span className="text-gray-400">IF </span>
                  employment_type = <span className="text-indigo-400">'contractor'</span><br/>
                  <span className="text-gray-400">THEN </span>
                  <span className="text-red-400">DENY</span>
                </div>
              )}

              {showValidation && (
                <div className="flex items-center gap-1.5" style={{ animation: 'apFadeIn 0.3s ease both' }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <circle cx="6" cy="6" r="5.5" fill="#f0fdf4" stroke="#4ade80" strokeWidth="1"/>
                    <path d="M3 6l2 2 4-4" stroke="#4ade80" strokeWidth="1.3"
                      strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="text-[0.68rem] text-green-600 font-medium">
                    Validated · 47 historical cases · 0 regressions
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="px-5 py-3.5 border-t border-gray-100 bg-gray-50/60 flex items-center justify-between">
        <p className="text-[0.68rem] text-gray-400">
          {showDeploy ? 'Awaiting deployment approval' : 'Analyzing guidelines...'}
        </p>
        {showDeploy && (
          <div style={{ animation: 'apFadeIn 0.35s ease both', display: 'inline-flex', alignItems: 'center',
            gap: '6px', fontSize: '0.72rem', fontWeight: 600, color: '#fff',
            background: '#818cf8', borderRadius: '8px', padding: '6px 12px', cursor: 'default' }}>
            Deploy
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
              <path d="M2 6h8M7 3l3 3-3 3" stroke="currentColor" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   PAGE
   ───────────────────────────────────────────── */

const pillars = [
  {
    num: '01',
    title: 'Encode',
    description: 'Any freeform decision description — policy documents, operational guidelines, SOPs, process notes — is parsed and converted into a deterministic, executable program. Same input always produces the same output.',
  },
  {
    num: '02',
    title: 'Verify',
    description: 'Every encoded rule is presented to your domain experts one condition at a time. Nothing goes live until a human has reviewed and approved it. Full identity and timestamp audit trail included.',
  },
  {
    num: '03',
    title: 'Evolve',
    description: "Business logic changes. Evident's proactive chat interface lets your team describe updates in plain language. The system proposes the change, validates it against historical decisions, and deploys on approval.",
  },
];

const EvidentPage = () => (
  <>
    {/* ── Hero ── */}
    <section className="pt-28 pb-20 sm:pt-32 sm:pb-24 bg-gray-950 border-b border-white/[0.06]">
      <Container>
        <div className="lg:grid lg:grid-cols-12 lg:gap-16 lg:items-center">

          {/* Text */}
          <div className="lg:col-span-6">
            <p className="inline-block text-xs font-semibold tracking-[0.12em] uppercase text-vermillion-600 mb-5">
              Decision Automation
            </p>
            <h1 className="text-[2.75rem] sm:text-5xl lg:text-[3.5rem] font-bold text-white tracking-tight leading-[1.05]">
              Vermillion Evident
            </h1>
            <p className="mt-4 text-xl font-medium text-vermillion-500 italic">
              Any decision guideline, made executable.
            </p>
            <p className="mt-6 text-[1.0625rem] leading-[1.75] text-zinc-400 max-w-lg">
              Decision logic lives in policy documents, guidelines, and operational SOPs. Read differently by every person, applied inconsistently at scale. Evident converts them into deterministic programs: same input, same output, every time.
            </p>
            <div className="mt-8 flex items-center gap-5">
              <Link
                to="/request-demo"
                className="inline-flex items-center justify-center py-2.5 px-5 text-sm font-semibold rounded-lg text-white bg-vermillion-600 hover:bg-vermillion-700 transition-colors"
              >
                Request Demo
              </Link>
              <a
                href="#how-it-works"
                onClick={(e) => { e.preventDefault(); document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'instant' }); }}
                className="text-sm font-medium text-zinc-500 hover:text-white transition-colors"
              >
                How it works ↓
              </a>
            </div>
          </div>

          {/* Static code card */}
          <div className="hidden lg:block lg:col-span-6 mt-12 lg:mt-0">
            <div className="rounded-2xl border border-white/[0.07] bg-white/[0.03] p-6">

              {/* Input */}
              <p className="text-[0.6rem] font-bold tracking-[0.12em] uppercase text-zinc-600 mb-3">Policy Document</p>
              <div className="rounded-lg border border-white/[0.06] bg-white/[0.04] px-4 py-3.5 mb-4">
                <p className="text-[0.82rem] text-zinc-400 leading-relaxed italic">
                  "The insured must have been continuously employed for at least{' '}
                  <span className="text-indigo-400 not-italic font-medium">14 months</span>{' '}
                  prior to onset. The{' '}
                  <span className="text-indigo-400 not-italic font-medium">90-day elimination period</span>{' '}
                  must be fully elapsed before benefits commence..."
                </p>
              </div>

              {/* Arrow */}
              <div className="flex items-center gap-2 mb-4 pl-1">
                <div className="h-px flex-1 bg-white/[0.06]" />
                <span className="text-[0.6rem] font-semibold tracking-[0.1em] uppercase text-vermillion-600 px-2">Evident encodes</span>
                <div className="h-px flex-1 bg-white/[0.06]" />
              </div>

              {/* Output */}
              <p className="text-[0.6rem] font-bold tracking-[0.12em] uppercase text-zinc-600 mb-3">Decision Program</p>
              <div className="rounded-lg border border-white/[0.06] bg-white/[0.04] px-4 py-3.5 font-mono text-[0.76rem] leading-loose">
                <div className="text-indigo-400 font-semibold">BENEFITS_ELIGIBILITY</div>
                <div className="text-zinc-500 pl-4">AND [</div>
                <div className="text-zinc-400 pl-8">employment_months <span className="text-green-400">≥ 14</span>,</div>
                <div className="text-zinc-400 pl-8">waiting_days <span className="text-green-400">≥ 90</span>,</div>
                <div className="text-zinc-400 pl-8">medical_evidence <span className="text-green-400">= provided</span></div>
                <div className="text-zinc-500 pl-4">]</div>
              </div>

              <div className="mt-4 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                <span className="text-[0.68rem] text-zinc-500">Deterministic · Same input, same output, every time</span>
              </div>

            </div>
          </div>

        </div>
      </Container>
    </section>

    {/* ── Three pillars ── */}
    <section id="how-it-works" className="py-16 sm:py-20 bg-gray-50">
      <Container>
        <div className="text-center mb-12">
          <p className="text-xs font-semibold tracking-[0.12em] uppercase text-vermillion-600 mb-3">
            How it works
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
            Three capabilities. One platform.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {pillars.map((p) => (
            <div
              key={p.num}
              className="bg-white rounded-2xl border border-gray-100 p-7 shadow-[0_2px_8px_rgba(0,0,0,0.03)]"
            >
              <p className="text-xs font-bold text-gray-300 tracking-[0.1em] mb-4">{p.num}</p>
              <h3 className="text-lg font-bold text-gray-900 mb-3">{p.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{p.description}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>

    {/* ── Encode ── */}
    <section className="py-20 sm:py-24 bg-white">
      <Container>
        <div className="lg:grid lg:grid-cols-12 lg:gap-16 lg:items-center">
          <div className="lg:col-span-5">
            <p className="text-xs font-semibold tracking-[0.12em] uppercase text-vermillion-600 mb-3">01 / Encode</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
              From any description<br/>to verified logic
            </h2>
            <p className="mt-5 text-base text-gray-500 leading-relaxed">
              Evident reads policy documents, operational guidelines, SOPs, and any other decision description. It parses the intent and generates a structured, deterministic decision program — not a probabilistic model, an exact executable program.
            </p>
            <p className="mt-4 text-base text-gray-500 leading-relaxed">
              The same input always produces the same output. Every condition is traceable to the source text it came from.
            </p>
          </div>
          <div className="mt-10 lg:mt-0 lg:col-span-7">
            <EncodeAnimation />
          </div>
        </div>
      </Container>
    </section>

    {/* ── Verify ── */}
    <section className="py-20 sm:py-24 bg-gray-50">
      <Container>
        <div className="lg:grid lg:grid-cols-12 lg:gap-16 lg:items-center">
          <div className="lg:col-span-7 lg:order-1">
            <VerifyAnimation />
          </div>
          <div className="mt-10 lg:mt-0 lg:col-span-5 lg:order-2">
            <p className="text-xs font-semibold tracking-[0.12em] uppercase text-vermillion-600 mb-3">02 / Verify</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
              Every rule reviewed<br/>before it goes live
            </h2>
            <p className="mt-5 text-base text-gray-500 leading-relaxed">
              No rule handles a live decision until a domain expert has reviewed and approved it. Evident presents each encoded condition one at a time — the rule itself, the source text it came from, and the logic structure it fits into.
            </p>
            <p className="mt-4 text-base text-gray-500 leading-relaxed">
              This isn't an optional step. The verification gate is built into the workflow. Every approved rule carries the reviewer's identity and timestamp.
            </p>
          </div>
        </div>
      </Container>
    </section>

    {/* ── Evolve ── */}
    <section className="py-20 sm:py-24 bg-white">
      <Container>
        <div className="lg:grid lg:grid-cols-12 lg:gap-16 lg:items-center">
          <div className="lg:col-span-5">
            <p className="text-xs font-semibold tracking-[0.12em] uppercase text-vermillion-600 mb-3">03 / Evolve</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
              Business logic changes.<br/>Evident keeps up.
            </h2>
            <p className="mt-5 text-base text-gray-500 leading-relaxed">
              When guidelines change, your team describes the update in plain language through Evident's chat interface. The system parses the intent, proposes the exact program change, and validates it against historical decisions before presenting it for approval.
            </p>
            <p className="mt-4 text-base text-gray-500 leading-relaxed">
              No engineering required. The same conversation that surfaces the need is the mechanism that drives the update.
            </p>
          </div>
          <div className="mt-10 lg:mt-0 lg:col-span-7">
            <EvolveAnimation />
          </div>
        </div>
      </Container>
    </section>

    {/* ── CTA ── */}
    <section className="py-20 bg-gray-950">
      <Container>
        <div className="text-center max-w-xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
            Ready to encode your decision logic?
          </h2>
          <p className="mt-4 text-base text-gray-400 leading-relaxed">
            See how Evident handles your specific guidelines — policy documents, operational rules, or SOPs.
          </p>
          <div className="mt-8 flex items-center justify-center gap-5">
            <Link
              to="/request-demo"
              className="inline-flex items-center justify-center py-2.5 px-6 text-sm font-semibold rounded-lg text-white bg-vermillion-600 hover:bg-vermillion-700 transition-colors"
            >
              Request Demo
            </Link>
            <a
              href="/#solutions"
              className="text-sm font-medium text-gray-400 hover:text-gray-200 transition-colors"
            >
              ← Back to products
            </a>
          </div>
        </div>
      </Container>
    </section>
  </>
);

export default EvidentPage;
